import { useState, useCallback, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import type { RootState } from '@/store/store'
import { InlineEditActions } from '@/inline-edit/store/slice'
import { selectInlineEditPendingReplacement } from '@/inline-edit/store/selectors'

type UseDiffReviewProps = {
  conversationId: string
  getCurrentMarkdown: () => string
}

type DiffReviewState = {
  oldMarkdown: string
  newMarkdown: string
} | null

/**
 * Manages the diff review lifecycle: watches for pending replacements from the BE,
 * captures the current markdown as the "old" version, and exposes accept/reject
 * to clear state. Does not interact with the editor — the parent handles that.
 */
export function useDiffReview({ conversationId, getCurrentMarkdown }: UseDiffReviewProps) {
  const appDispatch = useAppDispatch()
  const pendingReplacement = useAppSelector((state: RootState) => selectInlineEditPendingReplacement(state, { conversationId }))

  const [reviewState, setReviewState] = useState<DiffReviewState>(null)

  // When pendingReplacement arrives, capture the current markdown and enter review mode
  useEffect(() => {
    if (!pendingReplacement) return
    const oldMarkdown = getCurrentMarkdown()
    setReviewState({ oldMarkdown, newMarkdown: pendingReplacement.content })
  }, [pendingReplacement, getCurrentMarkdown])

  const handleAccept = useCallback(() => {
    setReviewState(null)
    appDispatch(InlineEditActions.clearPendingReplacement({ conversationId }))
    appDispatch(InlineEditActions.clearSelection({ conversationId }))
  }, [conversationId, appDispatch])

  const handleReject = useCallback(() => {
    setReviewState(null)
    appDispatch(InlineEditActions.clearPendingReplacement({ conversationId }))
    appDispatch(InlineEditActions.clearSelection({ conversationId }))
  }, [conversationId, appDispatch])

  return {
    isReviewing: reviewState !== null,
    oldMarkdown: reviewState?.oldMarkdown ?? '',
    newMarkdown: reviewState?.newMarkdown ?? '',
    handleAccept,
    handleReject,
  }
}
